import React from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, ShieldCheck, UserCheck, AlertTriangle, MessageSquare, Info } from 'lucide-react';
import { SchemaOrg } from '@/components/utils/SchemaOrg';

const TermsOfServicePage = () => {
  const location = useLocation();
  const pageTitle = "Terms of Service | WebFiny";
  const pageDescription = "Read the official Terms of Service for using WebFiny's PDF APIs, platform, and related services. Understand your rights and obligations.";
  const canonicalUrl = `${window.location.origin}${location.pathname}`;
  const lastUpdated = "June 15, 2025";

  const sections = [
    { 
      id: "introduction",
      icon: <Info className="w-6 h-6 mr-3 text-primary" />,
      title: "1. Introduction & Acceptance",
      content: `Welcome to WebFiny! These Terms of Service ("Terms") govern your access to and use of the WebFiny website (webfiny.com), our Application Programming Interfaces (APIs), software, tools, and any related services (collectively, the "Services") provided by FinyCore Inc. ("WebFiny", "we", "us", or "our"). By accessing or using our Services, you agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you may not access or use our Services. These Terms apply to all visitors, users, and others who access or use the Service.`
    },
    {
      id: "services",
      icon: <FileText className="w-6 h-6 mr-3 text-primary" />,
      title: "2. Description of Services",
      content: `WebFiny provides a suite of APIs and tools for PDF document processing, including but not limited to PDF merging, splitting, compression, conversion, data extraction, and document automation. We grant you a limited, non-exclusive, non-transferable, revocable license to use our Services in accordance with these Terms and any applicable API documentation or usage guidelines. We reserve the right to modify, suspend, or discontinue any aspect of the Services at any time, with or without notice, though we will strive to provide notice of material changes.`
    },
    {
      id: "accounts",
      icon: <UserCheck className="w-6 h-6 mr-3 text-primary" />,
      title: "3. User Accounts & Responsibilities",
      content: `To access certain features of our Services, you may be required to create an account. You are responsible for maintaining the confidentiality of your account credentials (including API keys) and for all activities that occur under your account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate. You must notify us immediately of any unauthorized use of your account. You are responsible for all data, content, and materials you submit or process through our Services ("Your Content") and for ensuring that Your Content complies with all applicable laws and does not infringe upon the rights of any third party.`
    },
    {
      id: "usage",
      icon: <ShieldCheck className="w-6 h-6 mr-3 text-primary" />,
      title: "4. Acceptable Use & Prohibitions",
      content: `You agree not to use the Services for any unlawful purpose or in any way that could harm, disable, overburden, or impair the Services or interfere with any other party's use and enjoyment of the Services. Prohibited activities include, but are not limited to: (a) attempting to reverse engineer, decompile, or disassemble any part of the Services; (b) using the Services to transmit spam, malware, or other malicious code; (c) infringing upon intellectual property rights; (d) processing illegal, harmful, or offensive content; (e) violating any applicable laws or regulations; (f) attempting to gain unauthorized access to our systems or user accounts; (g) reselling or sublicensing the Services without our express written permission.`
    },
    {
      id: "fees",
      icon: <Info className="w-6 h-6 mr-3 text-primary" />,
      title: "5. Fees, Payments, and Subscriptions",
      content: `Certain Services or usage tiers may require payment of fees. All applicable fees will be clearly disclosed to you prior to purchase or subscription. You agree to pay all fees incurred in connection with your account in a timely manner. Fees are typically billed on a subscription basis (e.g., monthly or annually) or based on usage (e.g., API calls). All fees are non-refundable except as expressly stated in these Terms or as required by applicable law. We reserve the right to change our fees and payment policies upon reasonable notice. Failure to pay fees may result in suspension or termination of your access to paid Services.`
    },
    {
      id: "intellectual-property",
      icon: <ShieldCheck className="w-6 h-6 mr-3 text-primary" />,
      title: "6. Intellectual Property Rights",
      content: `WebFiny and its licensors retain all right, title, and interest in and to the Services, including all underlying software, technology, documentation, and trademarks. These Terms do not grant you any rights to our trademarks or other intellectual property except for the limited license to use the Services as described herein. You retain all ownership rights to Your Content that you process through our Services. You grant WebFiny a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and process Your Content solely for the purpose of providing and improving the Services to you.`
    },
    {
      id: "termination",
      icon: <AlertTriangle className="w-6 h-6 mr-3 text-primary" />,
      title: "7. Termination",
      content: `We may terminate or suspend your access to our Services immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach these Terms. Upon termination, your right to use the Services will immediately cease. If you wish to terminate your account, you may do so by contacting us or through your account settings, if available. All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.`
    },
    {
      id: "disclaimers",
      icon: <AlertTriangle className="w-6 h-6 mr-3 text-primary" />,
      title: "8. Disclaimers & Limitation of Liability",
      content: `THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT ANY WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF PERFORMANCE. WEBFiny DOES NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, SECURE, ERROR-FREE, OR THAT DEFECTS WILL BE CORRECTED. IN NO EVENT SHALL WEBFiny, ITS DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, OR AFFILIATES, BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM (I) YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICE; (II) ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON THE SERVICE; (III) ANY CONTENT OBTAINED FROM THE SERVICE; AND (IV) UNAUTHORIZED ACCESS, USE OR ALTERATION OF YOUR TRANSMISSIONS OR CONTENT, WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE) OR ANY OTHER LEGAL THEORY, WHETHER OR NOT WE HAVE BEEN INFORMED OF THE POSSIBILITY OF SUCH DAMAGE.`
    },
    {
      id: "indemnification",
      icon: <ShieldCheck className="w-6 h-6 mr-3 text-primary" />,
      title: "9. Indemnification",
      content: `You agree to defend, indemnify and hold harmless WebFiny and its licensee and licensors, and their employees, contractors, agents, officers and directors, from and against any and all claims, damages, obligations, losses, liabilities, costs or debt, and expenses (including but not limited to attorney's fees), resulting from or arising out of a) your use and access of the Service, by you or any person using your account and password; b) a breach of these Terms, or c) Content posted on the Service.`
    },
    {
      id: "governing-law",
      icon: <Info className="w-6 h-6 mr-3 text-primary" />,
      title: "10. Governing Law & Dispute Resolution",
      content: `These Terms shall be governed and construed in accordance with the laws of the State of Delaware, United States, without regard to its conflict of law provisions. Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of these Terms will remain in effect. These Terms constitute the entire agreement between us regarding our Service, and supersede and replace any prior agreements we might have had between us regarding the Service. Any disputes arising from these Terms or the Services will be resolved through binding arbitration in accordance with the rules of the American Arbitration Association, rather than in court, except that you may assert claims in small claims court if your claims qualify.`
    },
    {
      id: "changes",
      icon: <Info className="w-6 h-6 mr-3 text-primary" />,
      title: "11. Changes to Terms",
      content: `We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion. By continuing to access or use our Service after any revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, you are no longer authorized to use the Service.`
    },
    {
      id: "contact",
      icon: <MessageSquare className="w-6 h-6 mr-3 text-primary" />,
      title: "12. Contact Information",
      content: `If you have any questions about these Terms, please contact us at: FinyCore Inc., 123 Innovation Drive, Tech Park, Suite 404, Future City, FC 98765, World. Email: legal@webfiny.com.`
    }
  ];

  return (
    <>
      <SchemaOrg
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": pageTitle,
            "description": pageDescription,
            "url": canonicalUrl,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": canonicalUrl
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": window.location.origin },
                { "@type": "ListItem", "position": 2, "name": "Terms of Service" }
              ]
            },
            "datePublished": "2023-01-01", 
            "dateModified": "2025-06-15", 
            "publisher": {
              "@type": "Organization",
              "name": "WebFiny",
              "logo": {
                "@type": "ImageObject",
                "url": "https://storage.googleapis.com/hostinger-horizons-assets-prod/34687cef-4e75-459d-b6c8-7e9a5f71ffdb/acacfe7044a1c9c74538c545cc0cae87.png"
              }
            }
          }
        ]}
      />
      <div className="bg-gradient-to-b from-background to-muted/30 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 md:mb-16"
          >
            <FileText className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-primary via-accent to-secondary mb-4">
              Terms of Service
            </h1>
            <p className="text-lg text-muted-foreground">Last Updated: {lastUpdated}</p>
            <p className="text-md text-muted-foreground max-w-2xl mx-auto mt-2">
              Please read these Terms of Service carefully before using WebFiny's services. Your access to and use of the Service is conditioned upon your acceptance of and compliance with these Terms.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto bg-card p-6 sm:p-8 md:p-10 rounded-xl shadow-2xl">
            <nav className="mb-10">
              <h2 className="text-xl font-semibold text-foreground mb-3">Table of Contents</h2>
              <ul className="space-y-1 list-decimal list-inside text-primary">
                {sections.map(section => (
                  <li key={section.id}>
                    <a href={`#${section.id}`} className="hover:underline hover:text-accent transition-colors">
                      {section.title.substring(section.title.indexOf(" ") + 1)}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            {sections.map((section, index) => (
              <motion.section
                key={section.id}
                id={section.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="mb-8 scroll-mt-24" 
              >
                <h2 className="flex items-center text-2xl font-semibold text-foreground mb-4 pb-2 border-b border-border">
                  {section.icon} {section.title}
                </h2>
                <div className="prose prose-lg dark:prose-invert max-w-none text-muted-foreground prose-headings:text-foreground prose-a:text-primary hover:prose-a:underline prose-strong:text-foreground">
                  <p>{section.content}</p>
                </div>
              </motion.section>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsOfServicePage;